import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { MessageAttendanceList, Messages, newMessage } from "../../model/Message";
import { alertOpen, AlertValue } from "../../features/alert.slice";
import messagesQuery from "../../firestore/Messages.query";
import { auth } from "../../firebase";
import { Company } from "../../model/Companies";
import { Spiner } from "../../components/spinner/Spiner";
import { ReplyButton } from "../../model/ReplyButton";
import { AttendanceResultTally } from "../../components/replyResultTally/AttendanceResultTally";
import { convertDateToJapaneseString, convertTimeToString } from "../../utility/ConvertUtily";
import Button from "../../components/button/Button";
import { ParticipantDeleteModal } from "./DistributionUserDeleteModal";
import { ConfirmAttendanceModal } from "./ConfirmAttendanceModalProps";
import { requestMunicipalityConfirmed, requestPointHistory, requestRdbMessagesAdminCount } from "../../api/Messages.api";
import { PointsAwardedModal } from "./PointsAwardedModal";
import companiesQuery from "../../firestore/Companies.query";
import { checkOption, isOptionActive } from "../../controller/OptionController";
import { requestEventPointAwarded } from "../../api/Point.api";
import { ConstMessage } from "../../config/const";

export const MessageAttendance = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // ログイン中のユーザー情報
  const currentUser = auth.currentUser;
  // URLパラメータからmessageId取得
  const { messageId } = useParams<{ messageId: string }>();

  const [isLoad, setIsLoad] = useState(true);
  const [company, setCompany] = useState<Company>();
  const [replyButtonSelected, setReplyButtonSelected] = useState<ReplyButton | undefined>();

  const [replyButtonNames, setReplyButtonNames] = useState<string[]>([]);
  const [message, setMessage] = useState<Messages>(newMessage);
  const [open, setOpen] = useState(false);
  const [selectedUserData, setSelectedUserData] = useState<MessageAttendanceList | null>(null);
  const [deleteUserList, setDeleteUserList] = useState<MessageAttendanceList[]>([]);
  const [isMunicipalityConfirmed, setIsMunicipalityConfirmed] = useState<boolean | undefined>(undefined);
  const [isStaffConfirmed, setIsStaffConfirmed] = useState<boolean | undefined>(undefined);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isPointOpen, setIsPointOpen] = useState(false);
  const [staffConfirmed, setStaffConfirmed] = useState<boolean | undefined>(undefined);

  const handleClickOpen = (item: MessageAttendanceList) => {
    setSelectedUserData(item);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUserData(null);
  };

  const handleConfirmOpen = () => {
    setIsConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setIsConfirmOpen(false);
  };

  const [isListLoading, setIsListLoading] = useState(false);

  const handleConfirm = async () => {
    try {
      handleConfirmClose();
      setIsListLoading(true);
      const updatedConfirmedFlg = !isMunicipalityConfirmed;
      await messagesQuery.setConfirmed(messageId!, updatedConfirmedFlg);
      setIsMunicipalityConfirmed(updatedConfirmedFlg);
  
      let resultSuccess = true;
      let pointAssignmentResultSuccess = true;

      // 編集確定時の出欠情報を保存
      const result = await requestMunicipalityConfirmed(company!.id, message.id, updatedConfirmedFlg);
      console.log(result);
      resultSuccess = result.success;
      if (message.pointSettingFlg) {
        // 編集確定時のポイント履歴を保存
        const pointAssignmentResult = await requestPointHistory(company!.id, updatedConfirmedFlg, message.pointId! ,message.id);
        console.log(pointAssignmentResult);
        pointAssignmentResultSuccess = pointAssignmentResult.success;
      }

      if(result.success){
        // stateを更新して一覧取得処理を動かす
        setMessage({...message, municipalityConfirmedFlg: updatedConfirmedFlg});
      }

      if(!(resultSuccess && pointAssignmentResultSuccess)){
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_UNEXPECTED, "danger")));
      }
    } catch (err) {
      console.error("Error updating confirmation status: ", err);
    }
  };
  

  const handlePointOpen = () => {
    setIsPointOpen(true);
  };

  const handlePointClose = () => {
    setIsPointOpen(false);
  };

  const handlePointAwarded = async () => {
    try {
      const result = await requestEventPointAwarded(company!.id, messageId ?? '');

      if (result.success) {
        // 付与フラグ更新
        setMessage({...message, pointAwardedFlg: true});

        dispatch(alertOpen(AlertValue("ポイントを付与しました。", "success")));
      } else {
        dispatch(alertOpen(AlertValue("ポイントを付与できませんでした。", "danger")));
      }

      handlePointClose();
    } catch (err: any) {
      dispatch(alertOpen(AlertValue("ポイントの付与に失敗しました。", "danger")));
    }
  };

  const [optionFlg, setOptionFlg] = useState<{ point: boolean }>({
    point: false,
  });

  // オプション設定出し分け
  async function fetchOptions() {
    const options = await companiesQuery.getOptionContractList(currentUser?.tenantId!);
    // ポイント管理のステータス、期間チェック
    const pointOption = options.find((option) => option.optionId === 'point');
    const point = pointOption ? isOptionActive(pointOption) : false;
    return { point };
  }

  useEffect(() => {
    async function loadOptions() {
      const result = await fetchOptions();
      setOptionFlg(result);
    }
  
    loadOptions();
  }, []);

  // お知らせデータ作成
  useEffect(() => {
    const f = async () => {
      try {
        if (messageId) {
          // 団体を取得
          const company = await companiesQuery.getCompany(currentUser?.tenantId!);
          setCompany(company);

          // お知らせ取得
          const message = await messagesQuery.getMessage(messageId);
          message.authorName = message.authorName ?? '';

          // 返信ボタン取得
          const replyButtonSelected = await messagesQuery.getReplyButton(messageId);
          setReplyButtonSelected(replyButtonSelected);

          // 返信ボタン名
          const replyButtonNames = [...new Set(replyButtonSelected?.buttonName.filter((name): name is string => name !== undefined && name !== ""))];
          setReplyButtonNames(replyButtonNames);

          const topic = await messagesQuery.getTopic(messageId);
          const topicIds = topic.map(t => t.id);
          if (message.select === 'token' && topicIds.length > 0) {
            message.topic = [];
          } else {
            message.topic = topicIds;
          }
          setIsMunicipalityConfirmed(message.municipalityConfirmedFlg ?? false);
          setIsStaffConfirmed(message.staffConfirmedFlg ?? false);
          setMessage(message);
          setIsLoad(false);
        } else {
          throw new Error();
        }
      } catch (err: any) {
        let errorMessage = err.error;
        if (errorMessage === "ERR_NETWORK") {
          dispatch(alertOpen(AlertValue("ネットワークに接続できません。", "danger")));
        } else {
          dispatch(alertOpen(AlertValue("お知らせを取得できません", "danger")));
        }
      }
    };
    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId]);

  useEffect(() => {
    const fetchMunicipalityConfirmed = async () => {
      // const confirmed = await messagesQuery.getMunicipalityConfirmed(messageId!);
      setIsMunicipalityConfirmed(message.municipalityConfirmedFlg);
    };
    const fetchStaffConfirmed = async () => {
      // const confirmed = await messagesQuery.getStaffConfirmed(messageId!);
      setIsStaffConfirmed(message.staffConfirmedFlg ?? false);
    };
    const staffCheck = async () => {
      // イベントサポート側で出欠確定可能か
      if (message.select === 'topic') {
        const result = await requestRdbMessagesAdminCount(message.id)
        if (result.success) {
          if (result.data > 0) {
            // 配信先の中にグループ管理者が存在する場合
            setStaffConfirmed(true);
          } else {
            setStaffConfirmed(false);
          }
        } else {
          console.log(result.data)
          dispatch(alertOpen(AlertValue("イベントサポートの管理者の取得に失敗しました。", "danger")));
        }
      }else{
        // 個別配信
        if(company){
          const adminFlg = await messagesQuery.getUserSearch(message.authorUid, company!.id);
          if (adminFlg) {
            setStaffConfirmed(true);
          } else {
            setStaffConfirmed(false);
          }
        }
      }
    };
    fetchMunicipalityConfirmed();
    fetchStaffConfirmed();
    staffCheck();
  }, [message]);
  
  return (
    <main>
      <div className="flex flex-col h-full">
        <nav className="flex items-start px-4 py-3" aria-label="Breadcrumb">
          <div
            className="inline-flex items-center space-x-3 text-sm font-medium text-gray-900 cursor-pointer"
            onClick={() => navigate(`/message/${messageId}/info`)}
          >
            <ChevronLeftIcon className="-ml-2 h-5 w-5 text-gray-400" aria-hidden="true" />
            <span>詳細へ</span>
          </div>
        </nav>
        <div className="px-3 sm:px-6 lg:px-8 flex-grow">
          {isLoad ? (
            <Spiner /> // ローディングスピナーを表示
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-bold text-gray-900">出欠一覧</h2>
                <div className="flex space-x-2">
                  <Button size="sm" onClick={() => navigate(`/message/${messageId}/info?scrollTo=deliveryResult`)}>
                    配信先一覧
                  </Button>
                  <Button size="sm" onClick={() => navigate(`/message/${messageId}/reply`)}>
                    返信一覧
                  </Button>
                  <Button
                    size="sm"
                    disabled={!(
                      message?.questionnaireFlg &&
                      message?.eventEndAt &&
                      new Date() > new Date(message.eventEndAt)
                    )}
                    className={!(
                      message?.questionnaireFlg &&
                      message?.eventEndAt &&
                      new Date() > new Date(message.eventEndAt)
                    ) ? "opacity-50 cursor-not-allowed" : ""}
                    onClick={() => navigate(`/message/${messageId}/questionnaireResults`)}
                  >
                    アンケート結果
                  </Button>
                  <Button
                    size="sm"
                    disabled={!(message?.eventFlg && message?.municipalityConfirmedFlg)}
                    className={!(message?.eventFlg && message?.municipalityConfirmedFlg) ? "opacity-50 cursor-not-allowed" : ""}
                    onClick={() => navigate(`/eventParticipation/${messageId}`)}
                  >
                    イベント参加履歴一覧
                  </Button>
                </div>
              </div>
              <div className="mt-7"></div>
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {message.title}
                </h2>
              </div>
              <div className="mt-3"></div>
              　イベント開催日時: {convertDateToJapaneseString(new Date(message?.eventAt))} {convertTimeToString(new Date(message?.eventAt))}～{convertTimeToString(new Date(message?.eventEndAt))}
              {staffConfirmed !== undefined && isStaffConfirmed !== undefined ? (
                <span className="text-black font-bold">
                  　イベントサポート：
                  {
                    staffConfirmed && isStaffConfirmed ? (
                      <span className="text-indigo-600">出欠確定済み</span>
                    ) : staffConfirmed && !isStaffConfirmed ? (
                      <span className="text-red-600">出欠未確定</span>
                    ) : !staffConfirmed ? (
                      <span className="text-black-600">管理者なし</span>
                    ) : <></>
                  }
                </span>
              ) : null}
              <AttendanceResultTally
                currentUser={currentUser}
                message={message}
                replyButton={replyButtonSelected}
                replyButtonNames={replyButtonNames}
                isListLoading={isListLoading}
                setIsListLoading={setIsListLoading}
                addUserFlg={location.state == null ? false : location.state.addFlg}
                deleteUserList={deleteUserList}
                setDeleteUserList={setDeleteUserList}
                handleClickOpen={handleClickOpen}
              />
              <div className="flex justify-end space-x-2 mt-4 mb-4">
                {!isMunicipalityConfirmed && (
                  <Button onClick={() => navigate(`/message/${messageId}/attendance/userSearch`)}>
                    利用者検索　
                  </Button>
                )}
                <Button 
                  onClick={handleConfirmOpen} 
                  disabled={message.pointAwardedFlg}
                >
                  {isMunicipalityConfirmed ? "編集確定解除" : "編集確定　　"}
                </Button>
  
                {optionFlg.point && message.pointSettingFlg && (
                  <Button 
                    onClick={async () => {
                      if (await checkOption("point", dispatch)) {
                        handlePointOpen();
                        }
                      }
                    }
                    disabled={message.pointAwardedFlg || !isMunicipalityConfirmed || isListLoading}
                  >
                    ポイント付与
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <ParticipantDeleteModal
        open={open}
        toggle={handleClose}
        closeOnClickOutside={true}
        tenantId={message.tenantId}
        messageId={message.id}
        isStaffConfirmed={isStaffConfirmed ?? false}
        userData={selectedUserData}
        deleteUserList={deleteUserList}
        setDeleteUserList={setDeleteUserList}
        children={undefined}      
      />
      <ConfirmAttendanceModal
        open={isConfirmOpen}
        toggle={handleConfirmClose}
        closeOnClickOutside={true}
        handleConfirm={handleConfirm}
        isLoad={false}
        children={undefined} 
        isMunicipalityConfirmed={isMunicipalityConfirmed ?? false}      
        isStaffConfirmed={isStaffConfirmed ?? false}  
        staffConfirmed={staffConfirmed ?? false}  
      />
      <PointsAwardedModal
        open={isPointOpen}
        toggle={handlePointClose}
        closeOnClickOutside={true}
        handle={handlePointAwarded}
        isLoad={false}
        children={undefined} 
        isMunicipalityConfirmed={isMunicipalityConfirmed ?? false}      
      />
    </main>
  );  
};